import { useEffect, type MutableRefObject, type RefObject } from 'react';
import { releaseWallArtTextures } from './render/wallArtFacing';
import { createMusicNotes } from './render/musicNotes';
import { onInstrumentMelody } from './interaction/instrumentMelody';
import { PanFirstPinch } from './interaction/cameraGestures';
import * as PIXI from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import posthog from 'posthog-js';
import { getWallTexturePath, getFloorTexturePath } from '../../data/furniture';
import { applyRoomTextureScale, applyWallSpriteTexture } from './render/textures';
import { drawTileGridOverlay } from './render/overlays';
import type { RoomSceneState } from './state/roomSceneState';
import { mapSceneSingleton } from '../map/mapScene';

// ── Scene-init effect: room PIXI scene setup/teardown on the shared map app ──
// Extracted verbatim from RoomCanvas (decomposition step: scene-init effect).
// The effect body is byte-identical to the original except that:
//  * RoomCanvas-owned refs, state flags and callbacks are received as parameters;
//  * the module-level scene state (roomViewport, roomContainer, rugContainer,
//    interiorContainer, cobwebContainer, cobwebSpriteMap) is both READ and
//    WRITTEN by this effect, so read-only getters (the useFurnitureRender
//    pattern) are not enough — it is accessed through `scene`, a live get/set
//    accessor object over the exact same module `let` bindings in RoomCanvas.
//    Every bare reference `x` became `scene.x`; this is the only mechanical
//    rewrite applied to the body;
//  * FLOOR_HEIGHT / ROOM_WORLD_SIZE are still used by other RoomCanvas code
//    (viewport re-centering effect), so they are passed in as parameters;
//  * drawTileGridOverlay (also in the deps array) is the same module import
//    RoomCanvas uses — identical binding, so the dep value is unchanged.
// Constants used ONLY by this effect moved here with it (WALL_ANCHOR_Y).

// Wall anchor Y — the bottom edge of the wall parallelogram (junction with floor)
// is at pixel y=303 out of 550. In normalized coords: 303/550 ≈ 0.551
const WALL_ANCHOR_Y = 303 / 550;

// Live view over RoomCanvas's module-level mutable scene state (get/set accessors).
export interface RoomPixiRoots {
  roomViewport: Viewport | null;
  roomContainer: PIXI.Container | null;
  rugContainer: PIXI.Container | null;
  interiorContainer: PIXI.Container | null;
  cobwebContainer: PIXI.Container | null;
  cobwebSpriteMap: Map<number, { outline: PIXI.Container; graphic: PIXI.Container; posKey: string }>;
}

export interface UseRoomSceneParams {
  mapObjectId: number;
  isCityHall: boolean;
  effectiveIsOwner: boolean | undefined;
  showGridMode: boolean;
  tileGridDebugEnabled: boolean;
  FLOOR_HEIGHT: number;
  ROOM_WORLD_SIZE: number;
  /**
   * Ref to module-level PIXI roots (viewport/containers).
   * Passed as *Ref so react-hooks/immutability allows writes (not React state).
   */
  pixiRootsRef: MutableRefObject<RoomPixiRoots>;
  /** Grouped mutable scene state (was ~40 useRefs). */
  sceneRef: MutableRefObject<RoomSceneState>;
  canvasContainerRef: RefObject<HTMLDivElement | null>;
  setIsSceneReady: (ready: boolean) => void;
}

export function useRoomScene({
  mapObjectId,
  isCityHall,
  effectiveIsOwner,
  showGridMode,
  tileGridDebugEnabled,
  FLOOR_HEIGHT,
  ROOM_WORLD_SIZE,
  pixiRootsRef,
  sceneRef,
  canvasContainerRef,
  setIsSceneReady,
}: UseRoomSceneParams): void {
  // Initialize room scene — reuse the map singleton PIXI app
  useEffect(() => {
    if (!canvasContainerRef.current) return;

    const app = mapSceneSingleton.app;
    if (!app) {
      // console.error('[RoomCanvas] No PIXI app available from map singleton');
      return;
    }

    // Capture once — ref identity is module-stable (roomPixiRoots); shared by init + cleanup.
    const roots = pixiRootsRef.current;

    let cancelled = false;
    let stopMusicNotes: (() => void) | null = null;
    const preventContextMenu = (e: Event) => {
      e.preventDefault();
    };

    const initRoom = async () => {
      // PostHog load-timing: mark the real moment of interior entry (post-training) and
      // reset the one-shot "fully loaded" guard for this fresh interior entry.
      sceneRef.current.analytics.interiorEnterTs = performance.now();
      sceneRef.current.analytics.didTrackInteriorFullyLoaded = false;

      // Change background to dark
      app.renderer.background.color = 0x111111;

      // Attach canvas to our container
      const canvas = app.canvas as HTMLCanvasElement;
      canvas.style.display = 'block';
      canvas.addEventListener('contextmenu', preventContextMenu);
      if (canvasContainerRef.current && !canvasContainerRef.current.contains(canvas)) {
        if (canvas.parentElement) {
          canvas.parentElement.removeChild(canvas);
        }
        canvasContainerRef.current.appendChild(canvas);
      }

      // Hide the map viewport (don't destroy it — MapCanvas owns it)
      if (mapSceneSingleton.viewport) {
        mapSceneSingleton.viewport.visible = false;
      }

      // Clear previous room content if any
      if (roots.roomViewport) {
        app.stage.removeChild(roots.roomViewport);
        roots.roomViewport.destroy({ children: true });
        roots.roomViewport = null;
        roots.roomContainer = null;
        roots.rugContainer = null;
        roots.interiorContainer = null;
        roots.cobwebContainer = null;
        roots.cobwebSpriteMap = new Map();
      }

      // Create viewport for pan/zoom
      const vp = new Viewport({
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        worldWidth: ROOM_WORLD_SIZE,
        worldHeight: ROOM_WORLD_SIZE,
        events: app.renderer.events,
      });
      roots.roomViewport = vp;
      app.stage.addChild(vp);

      vp.drag().wheel().decelerate();
      // Two fingers pan; only spreading/pinching them zooms (cameraGestures).
      vp.plugins.add('pinch', new PanFirstPinch(vp));
      vp.clampZoom({ minScale: 0.3, maxScale: 3.0 });

      // Create room container inside viewport
      const rc = new PIXI.Container();
      rc.sortableChildren = true; // Enable z-index sorting for overlay
      roots.roomContainer = rc;
      vp.addChild(rc);

      // Position room container at viewport center
      rc.position.set(ROOM_WORLD_SIZE / 2, ROOM_WORLD_SIZE / 2 - 100);

      // Calculate room center automatically from floor center
      // Floor is a diamond with center at (0, FLOOR_HEIGHT / 2) in room-local coords
      const roomCenterLocalX = 0; // Center of floor diamond horizontally
      const roomCenterLocalY = FLOOR_HEIGHT / 2; // Center of floor diamond vertically (250)
      
      // Convert to world coords by adding room container position
      const roomCenterWorldX = ROOM_WORLD_SIZE / 2 + roomCenterLocalX;
      const roomCenterWorldY = (ROOM_WORLD_SIZE / 2 - 100) + roomCenterLocalY;
      
      // Set zoom for "from high above" view - maximum zoom out
      // minScale = 0.3, maxScale = 3.0
      // Using minimum zoom (0.3) for the most distant view possible
      const targetZoom = 0.3;
      vp.setZoom(targetZoom);
      
      // Center viewport on room center after setting zoom
      vp.moveCenter(roomCenterWorldX, roomCenterWorldY);

      // Load initial textures from placed furniture (must have catalog IDs)
      // For houses: default walls (55) and floor (56) are auto-granted
      // For city halls: default walls (57) and floor (58) are Marble Palace
      // If the room already has a placed custom wall/floor, load THAT directly instead of
      // loading the default and then swapping. Loading the default first would leave its
      // (potentially huge, e.g. 4000x2000) texture resident in the GPU cache alongside the
      // custom one — doubling wall/floor texture memory and crashing the WebGL context on
      // memory-constrained devices (iOS Safari). See sceneRef.current.backdrop.initialWallFloorCatalogIds.
      const customWallId = sceneRef.current.backdrop.initialWallFloorCatalogIds.wall;
      const customFloorId = sceneRef.current.backdrop.initialWallFloorCatalogIds.floor;
      const initialWallId = customWallId ?? (isCityHall ? 57 : 55); // Marble Palace Walls for cityhall, default for house
      const initialFloorId = customFloorId ?? (isCityHall ? 58 : 56); // Marble Palace Floor for cityhall, default for house
      // Record what we loaded so the wall/floor swap effect treats it as already-applied
      // and does not reload (which would re-introduce the duplicate-texture problem).
      sceneRef.current.backdrop.activeWallCatalogId = customWallId;
      sceneRef.current.backdrop.activeFloorCatalogId = customFloorId;
      const wallPath = getWallTexturePath(initialWallId);
      const floorPath = getFloorTexturePath(initialFloorId);
      
      const floorTexPromise = PIXI.Assets.load(floorPath);
      const wallTexPromise = PIXI.Assets.load(wallPath);
      sceneRef.current.backdrop.loadedTexturePaths.add(floorPath);
      sceneRef.current.backdrop.loadedTexturePaths.add(wallPath);
      const floorTex = await floorTexPromise;
      const wallTex = await wallTexPromise;

      if (cancelled) return;

      // Wall left: junction (right-bottom of parallelogram) at floor top vertex
      // Pixel analysis: right edge bottom is at ~(499, 303) in 500×550 image
      // Anchor at that point: (1.0, 303/550)
      const wallLeft = new PIXI.Sprite(wallTex);
      wallLeft.anchor.set(0.0, WALL_ANCHOR_Y);
      wallLeft.position.set(0, 0);
      rc.addChild(wallLeft);
      sceneRef.current.sprites.wallLeft = wallLeft;

      // Wall right: junction (left-bottom of parallelogram) at floor top vertex
      // Pixel analysis: left edge bottom is at ~(0, 303) in 500×550 image
      // Anchor at that point: (0.0, 303/550)
      const wallRight = new PIXI.Sprite(wallTex);
      wallRight.anchor.set(0.0, WALL_ANCHOR_Y);
      wallRight.position.set(0, 0);
      rc.addChild(wallRight);
      sceneRef.current.sprites.wallRight = wallRight;
      applyWallSpriteTexture(wallRight, wallTex, false);
      applyWallSpriteTexture(wallLeft, wallTex, true);

      // Mark wall sprites interactive — full long-press logic attached in a separate useEffect
      // so it has access to React state (viewport, radial menu callbacks) via refs.
      wallLeft.eventMode = 'static';
      wallLeft.cursor = 'pointer';
      wallRight.eventMode = 'static';
      wallRight.cursor = 'pointer';

      // Floor sprite: top vertex anchor — renders ON TOP of walls (floor overlaps junction)
      const floor = new PIXI.Sprite(floorTex);
      floor.anchor.set(0.5, 0.0);
      floor.position.set(0, 0);
      applyRoomTextureScale(floor, floorTex); // apply 1/4 scale immediately — same as walls
      rc.addChild(floor);
      sceneRef.current.sprites.floor = floor;
      floor.eventMode = 'static';
      floor.cursor = 'pointer';

      if (tileGridDebugEnabled) {
        const gridOverlay = new PIXI.Graphics();
        drawTileGridOverlay(gridOverlay);
        rc.addChild(gridOverlay);
        sceneRef.current.overlays.tileGrid = gridOverlay;
      }

      // Occupied tiles overlay: shows furniture footprints (can be disabled via SHOW_OCCUPIED_TILES_OVERLAY)
      const occupiedOverlay = new PIXI.Graphics();
      // Set very high z-index so overlay is always visible above all furniture
      occupiedOverlay.zIndex = 999999;
      // Hide overlay initially if grid mode is off
      occupiedOverlay.visible = showGridMode;
      rc.addChild(occupiedOverlay);
      sceneRef.current.overlays.occupiedTiles = occupiedOverlay;

      // Drag footprint overlay: green/red tile highlight shown during furniture drag
      const dragFpOverlay = new PIXI.Graphics();
      dragFpOverlay.zIndex = 999998;
      dragFpOverlay.visible = false;
      dragFpOverlay.eventMode = 'none';
      rc.addChild(dragFpOverlay);
      sceneRef.current.overlays.dragFp = dragFpOverlay;

      // Smart-guide lines for a dragged wall item: above the furniture, below admin overlays.
      const wallGuidesOverlay = new PIXI.Graphics();
      wallGuidesOverlay.zIndex = 999997;
      wallGuidesOverlay.visible = false;
      wallGuidesOverlay.eventMode = 'none';
      rc.addChild(wallGuidesOverlay);
      sceneRef.current.overlays.wallGuides = wallGuidesOverlay;

      // Admin tile selection overlay: shows selected tiles during admin editing mode
      const adminTileOverlay = new PIXI.Graphics();
      adminTileOverlay.zIndex = 1000000; // Above occupied tiles overlay
      adminTileOverlay.visible = false;
      adminTileOverlay.eventMode = 'none'; // Never intercept pointer events
      rc.addChild(adminTileOverlay);
      sceneRef.current.overlays.adminTile = adminTileOverlay;

      // Admin drag preview overlay: shows live rectangle during drag-paint
      const adminDragPreview = new PIXI.Graphics();
      adminDragPreview.zIndex = 1000001; // Above committed selection overlay
      adminDragPreview.visible = false;
      adminDragPreview.eventMode = 'none'; // Never intercept pointer events — critical to prevent PIXI hit-test confusion
      rc.addChild(adminDragPreview);
      sceneRef.current.overlays.adminDragPreview = adminDragPreview;

      // Rug layer: renders BELOW all other furniture (added to scene graph first)
      const rugC = new PIXI.Container();
      rugC.sortableChildren = true;
      rc.addChild(rugC);
      roots.rugContainer = rugC;

      // Interior objects container (with z-sorting) – renders ABOVE rugs
      const ic = new PIXI.Container();
      ic.sortableChildren = true;
      rc.addChild(ic);
      roots.interiorContainer = ic;

      // Make floor interactive for drag constraint
      floor.eventMode = 'static';

      // Enable interactions on stage
      app.stage.eventMode = 'static';
      app.stage.interactiveChildren = true;

      // Start ticker
      if (!app.ticker.started) {
        app.ticker.start();
      }

      // Rainbow notes over an instrument while it plays (render/musicNotes).
      const musicNotes = createMusicNotes(rc, app.ticker);
      const offMelody = onInstrumentMelody((playing) => {
        if (playing) musicNotes.start(playing.sprite, playing.catalogId);
        else musicNotes.stop();
      });
      stopMusicNotes = () => { offMelody(); musicNotes.destroy(); };

      if (!cancelled) {
        setIsSceneReady(true);
        posthog.capture('house_interior_entered', {
          map_object_id: mapObjectId,
          is_owner: !!effectiveIsOwner,
          is_city_hall: isCityHall,
        });
      }
    };

    initRoom();

    return () => {
      cancelled = true;
      stopMusicNotes?.();
      stopMusicNotes = null;

      // Cleanup room viewport from the shared app stage
      if (roots.roomViewport && app) {
        app.stage.removeChild(roots.roomViewport);
        roots.roomViewport.destroy({ children: true });
        roots.roomViewport = null;
        roots.roomContainer = null;
        roots.rugContainer = null;
        roots.interiorContainer = null;
        roots.cobwebContainer = null;
        roots.cobwebSpriteMap = new Map();
        sceneRef.current.overlays.tileGrid = null;
        sceneRef.current.overlays.occupiedTiles = null;
        sceneRef.current.overlays.dragFp = null;
        sceneRef.current.overlays.wallGuides = null;
        sceneRef.current.overlays.adminTile = null;
        sceneRef.current.overlays.adminDragPreview = null;
      }

      // Restore map viewport visibility
      if (mapSceneSingleton.viewport) {
        mapSceneSingleton.viewport.visible = true;
      }

      // Restore background
      if (app) {
        app.renderer.background.color = 0x77C0AF;
      }

      // Detach canvas (MapCanvas will re-attach on mount)
      if (app) {
        const canvas = app.canvas as HTMLCanvasElement;
        canvas.removeEventListener('contextmenu', preventContextMenu);
        if (canvas.parentElement) {
          canvas.parentElement.removeChild(canvas);
        }
        app.stage.eventMode = 'none';
        app.stage.interactiveChildren = false;
        app.ticker.stop();
        canvas.style.display = 'none';
      }

      sceneRef.current.furniture.markers.clear();
      sceneRef.current.overlays.tileGrid = null;
      sceneRef.current.overlays.occupiedTiles = null;
      sceneRef.current.overlays.dragFp = null;
      sceneRef.current.overlays.wallGuides = null;
      sceneRef.current.overlays.adminTile = null;
      sceneRef.current.overlays.adminDragPreview = null;

      releaseWallArtTextures();
      // Synchronous full texture cleanup: free GPU + CPU + caches.
      // Assets.unload() is NOT safe here (its async texture.destroy() races with
      // in-flight operations). Instead we do everything synchronously:
      // 1. source.unload() — free GPU texture data
      // 2. ImageBitmap.close() — free decoded image CPU memory
      // 3. Cache.remove() + Loader.promiseCache delete — ensure next Assets.load()
      //    re-fetches instead of returning stale/destroyed objects
      const loader = PIXI.Assets.loader;
      /* eslint-disable react-hooks/exhaustive-deps -- teardown needs live PIXI bag at unmount, not mount snapshot */
      for (const texPath of sceneRef.current.backdrop.loadedTexturePaths) {
        try {
          const texture = PIXI.Assets.get(texPath);
          if (texture?.source) {
            texture.source.unload(); // Free GPU
            if (texture.source.resource && typeof texture.source.resource.close === 'function') {
              texture.source.resource.close(); // Free decoded ImageBitmap CPU memory
            }
          }
        } catch { /* texture may already be gone */ }
        try { PIXI.Cache.remove(texPath); } catch { /* ignore */ }
        try {
          const absUrl = PIXI.path.toAbsolute(texPath);
          if (absUrl && loader.promiseCache[absUrl]) {
            delete loader.promiseCache[absUrl];
          }
        } catch { /* ignore */ }
      }
      sceneRef.current.backdrop.loadedTexturePaths.clear();
      // Also clear the alpha mask cache (canvas elements for hit-testing)
      sceneRef.current.caches.alphaMask.clear();
      /* eslint-enable react-hooks/exhaustive-deps */
    };
    // Тригер: mapObjectId + isCityHall (вхід/вихід кімнати). showGridMode /
    // tileGridDebugEnabled / effectiveIsOwner — НЕ додавати: toggle grid ≠ full
    // scene destroy/recreate. pixiRootsRef/sceneRef/canvasContainerRef/setIsSceneReady /
    // FLOOR_HEIGHT/ROOM_WORLD_SIZE — стабільні. .current у cleanup — live PIXI bag
    // на unmount (не mount snapshot).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- room entry only; grid flags would thrash
  }, [mapObjectId, isCityHall, drawTileGridOverlay, pixiRootsRef, sceneRef, canvasContainerRef, setIsSceneReady, FLOOR_HEIGHT, ROOM_WORLD_SIZE]);
}
